import { useState, useEffect } from "react";
import styles from "./style.module.scss";
import Image from "next/image";
import { parseHtml } from "@/utils/common";

interface Conditions {
  id: number;
  title: string;
  description: string;
}
interface UseCaseMainFlowProps {
  block: {
    title: string;
    Conditions: Conditions[];
  };
}

// ✅ Custom hook for responsive slidesPerView
const useSlidesPerView = () => {
  const [slidesPerView, setSlidesPerView] = useState(2.5);

  useEffect(() => {
    const updateSlidesPerView = () => {
      if (window.innerWidth < 640) {
        setSlidesPerView(1.2); // mobile
      } else if (window.innerWidth < 1024) {
        setSlidesPerView(2.5); // tablet
      } else {
        setSlidesPerView(2.5); // desktop & large
      }
    };

    updateSlidesPerView();
    window.addEventListener("resize", updateSlidesPerView);
    return () => window.removeEventListener("resize", updateSlidesPerView);
  }, []);

  return slidesPerView;
};

const UseCaseMainFlow: React.FC<UseCaseMainFlowProps> = ({ block }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const slidesPerView = useSlidesPerView();
  const totalSlides = block.Conditions.length;

  const goNext = () => {
    setActiveIndex((prev) => Math.min(prev + 1, totalSlides - 1));
  };

  const goPrev = () => {
    setActiveIndex((prev) => Math.max(prev - 1, 0));
  };

  return (
    <section className="relative z-[0] overflow-hidden py-[56px] lg:py-[80px] xl:py-[100px] bg-white before:content-[''] before:absolute before:h-[100%] before:inset-0 before:bg-[url('/images/client-bg.png')] before:bg-no-repeat before:bg-[length:100%_100%] before:bg-center before:bg-[#F0F8FF] before:-z-10">
      <div className="mx-auto container max-w-[1020px]">
        <h2 className="font-bold text-heading-5 text-primary-text mb-[40px]">
          {block.title ?? ""}
        </h2>
      </div>

      <div className={styles.sliderMain}>
        {/* Progress Line */}
        <div className={styles.paginationLine}>
          <div
            className={`${styles.paginationProgress} ${
              activeIndex >= 0 ? styles.active : ""
            }`}
            style={{
              width: `${((activeIndex + 1.4) / totalSlides) * 100}%`,
            }}
          />
        </div>

        {/* Slider */}
        <div
          className={`${styles.sliderContainer} container max-w-[1020px] mx-auto`}
        >
          <div className={styles.SliderWrapper}>
            <div
              className={styles.SliderTrack}
              style={{
                transform: `translateX(-${(activeIndex * 100) / slidesPerView}%)`,
              }}
            >
              {block?.Conditions?.map((slide, index) => (
                <div key={slide.id} className={styles.SliderCard}>
                  <div
                    className={`${styles.paginationLineBox} ${
                      index === activeIndex ? styles.active : ""
                    } font-bold text-subtitle-1`}
                  >
                    {index + 1 >= 10 ? index + 1 : `0${index + 1}`}
                  </div>
                  <h5 className="font-bold text-primary-text text-subtitle-1 mb-[12px] min-h-[50px] lg:min-h-[60px] line-clamp-2">
                    {slide.title}
                  </h5>
                  <p className="font-regular text-primary-text text-subtitle-2">
                    {slide.description ? parseHtml(slide.description) : ""}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className={`${styles.navigation} mt-[38px]`}>
            <div className="flex gap-[12px]">
              <div
                className="left-arrow w-[40px] h-[40px] bg-white flex items-center justify-center border border-[#B0D9F7] cursor-pointer group hover:border-[#0086E6] transition-all duration-300 ease-in-out"
                onClick={goPrev}
              >
                <Image
                  src="/images/use-cases/arrow-right.svg"
                  alt="arrow left"
                  width={24}
                  height={24}
                  className="block group-hover:hidden transition-all duration-300 ease-in-out"
                />
                <Image
                  src="/images/use-cases/arrow-right-active.svg"
                  alt="arrow left active"
                  width={24}
                  height={24}
                  className="hidden group-hover:block transition-all duration-300 ease-in-out"
                />
              </div>
              <div
                className="right-arrow w-[40px] h-[40px] bg-white flex items-center justify-center border border-[#B0D9F7] cursor-pointer group hover:border-[#0086E6] transition-all duration-300 ease-in-out"
                onClick={goNext}
              >
                <Image
                  src="/images/use-cases/arrow-left.svg"
                  alt="arrow right"
                  width={24}
                  height={24}
                  className="block group-hover:hidden transition-all duration-300 ease-in-out"
                />
                <Image
                  src="/images/use-cases/arrow-left-active.svg"
                  alt="arrow right active"
                  width={24}
                  height={24}
                  className="hidden group-hover:block transition-all duration-300 ease-in-out"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UseCaseMainFlow;
